<script setup lang="ts">
import type { ImgHTMLAttributes } from 'vue';
import type { Image, ImageCrop, ImageFocalPoints } from '~/types';

type GenericImageModifier = Record<string, unknown>;
type ImageModifiers = Expand<
  {
    center?: string;
    cc?: string;
    mode?: string;
  } & GenericImageModifier
>;

/* eslint-disable vue/require-default-prop */
type Props = {
  image: Image;
  aspectRatio?: string;
  format?: string;
  quality?: string | number;
  background?: string;
  fit?: string;
  modifiers?: ImageModifiers;
  preset?: string;
  provider?: string;
  sizes?: string;
  preload?: boolean;
  width?: string | number;
  height?: string | number;
  alt?: string;
  referrerpolicy?: ImgHTMLAttributes['referrerpolicy'];
  crossorigin?: 'anonymous' | 'use-credentials' | boolean;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'auto' | 'sync';
  isDecorative?: boolean;
  role?: 'presentation' | string;
};

const props = withDefaults(defineProps<Props>(), {
  quality: 80,
  provider: 'imageSharp',
  loading: 'lazy',
  format: 'webp',
  isDecorative: false,
  role: undefined
});

const root = useRoot();
const layout = useLayout();
const possibleDuplicateProps = ['width', 'height', 'modifiers', 'alt'];

const selectedCrop = computed(() => props.image.crops?.find((crop) => crop.alias === props.aspectRatio));
const cropHasCropCoordinate = computed(() => Boolean(selectedCrop.value?.cropCoordinates));

function getCropCoordinates(crop: ImageCrop): string {
  if (!crop.cropCoordinates) return getFocalPointCenter();

  const { x1, y1, x2, y2 } = crop.cropCoordinates;
  return `${x1},${y1},${x2},${y2}`;
}

function getFocalPointCenter(): string {
  const { left, top } = props.image.focalPoint ?? { left: 0.5, top: 0.5 };
  return props.modifiers?.center ?? `${left},${top}`;
}

function calculateImageDimension(dimension: 'width' | 'height') {
  if (selectedCrop.value) {
    return layout.isDesktop ? selectedCrop.value[dimension] : selectedCrop.value[dimension] / 2;
  }

  return props[dimension] ?? props.image[dimension];
}

const imageUrl = computed(() => {
  return props.image?.url ?? root.settings.content.generalFallbackImage?.url;
});

function calculateInternalSizes(): Record<string, string> {
  if (!props.sizes) return {};

  const screens = useNuxtApp().$img.options.screens;
  const sizes: Record<string, string> = {};

  if (typeof props.sizes === 'string') {
    for (const entry of props.sizes.split(/[\s,]+/).filter(Boolean)) {
      const [key, value] = entry.split(':');
      if (key && value) {
        sizes[key.trim()] = value.trim();
      }
    }
  } else {
    Object.assign(sizes, props.sizes);
  }

  const maxSizeIndex = Object.keys(sizes).reduce((max, key) => Math.max(max, Object.keys(screens).indexOf(key)), 0);

  Object.keys(screens).forEach((key, index) => {
    if (index < maxSizeIndex && !sizes[key]) {
      sizes[key] = '100vw';
    }
  });

  return sizes;
}

const internalSizes = calculateInternalSizes();

function mergeImageProperties(props: Props): Omit<Props, keyof typeof possibleDuplicateProps> & {
  width?: string | number;
  height?: string | number;
  alt?: string;
  focalPoint?: ImageFocalPoints;
} {
  const mergedProps: Props & {
    role?: 'presentation' | string;
  } = {
    image: props.image
  };

  Object.entries(props).forEach(([key, value]) => {
    if (!possibleDuplicateProps.includes(key)) {
      (mergedProps as Record<string, unknown>)[key] = value;
    }
  });

  // This is to enforce decorative images
  // for more context: https://bitbucket.org/novicell/cshf.frontend.headless/pull-requests/156
  const alt = props.isDecorative ? '' : (props.alt ?? props.image.additionalProperties?.alt);
  const role = props.isDecorative ? 'presentation' : props.role;
  const modifiers: ImageModifiers =
    selectedCrop.value && cropHasCropCoordinate.value
      ? { cc: getCropCoordinates(selectedCrop.value) }
      : {
          mode: props.modifiers?.mode ?? 'crop',
          center: getFocalPointCenter()
        };

  mergedProps.alt = alt;
  mergedProps.role = role;
  mergedProps.modifiers = modifiers;
  mergedProps.width = calculateImageDimension('width');
  mergedProps.height = calculateImageDimension('height');

  return mergedProps;
}

const defaultProps = computed(() => ({
  ...mergeImageProperties(props),
  src: useCdnBase(imageUrl.value || ''),
  internalSizes
}));
</script>

<template>
  <NuxtImg v-bind="defaultProps" />
</template>
